import { SALES_COPILOT } from 'chatspot-core/types/CopilotAssistantTypes';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAssistant } from '../../assistants/slices/assistants';
import { initializeAblyChannel } from '../../chat/actions/initializeAblyChannel';
import { selectAblyChannelId } from '../../chat/selectors/chatv2Selectors';
import { getBreezeCopilotAssistant, SALES_COPILOT_ASSISTANT } from '../../components/assistants/internal/constants/assistants';
import { useAssistants } from '../../components/assistants/internal/hooks/getAssistants';
import { THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V2 } from '../../constants/threads';
import { useIsUngatedForBreezeRebrand } from '../../gating/hooks/useIsUngatedForBreezeRebrand';
import { copilotV1ThreadMessagesForV2 } from '../../threads/actions/copilotV1ThreadMessagesForV2';
import { copilotV2GetThreadWithMessages } from '../../threads/actions/copilotV2GetThreadWithMessages';
export const useCopilotV2SwitchAblyThreads = () => {
  const ablyChannelId = useSelector(selectAblyChannelId);
  const dispatch = useDispatch();
  const {
    refetchAssistants
  } = useAssistants();
  const isUngatedForBreezeRebrand = useIsUngatedForBreezeRebrand();
  const handleAssistantSelection = useCallback((assistantType, customCopilotId) => {
    return refetchAssistants().then(newAssistants => {
      let assistantToSelect;
      assistantToSelect = newAssistants.find(assistant => assistant.id === customCopilotId) || getBreezeCopilotAssistant(isUngatedForBreezeRebrand);
      if (assistantType === SALES_COPILOT) {
        assistantToSelect = SALES_COPILOT_ASSISTANT;
      }
      if (assistantToSelect) {
        dispatch(updateAssistant(assistantToSelect));
      }
    }).catch(e => {
      Raven.captureException(e);
    });
  }, [dispatch, isUngatedForBreezeRebrand, refetchAssistants]);

  /**
   * Switches the current Ably thread to a new thread or creates a new one
   * @param {number} [args.threadId] - The ID of the thread to switch to. If not provided, a new thread will be created.
   * @returns {void}
   *
   * @description
   * This function detaches/unsubscribes from the current Ably channel and either:
   * 1. Creates a new thread if no threadId is provided
   * 2. Loads an existing thread if threadId is provided
   */
  const switchAblyThread = useCallback(({
    assistantType,
    customCopilotId,
    threadId,
    isReadOnly
  }) => {
    if (!ablyChannelId) {
      return Promise.reject(Raven.captureMessage('Failure to switch Ably thread via switchAblyThread - no ablyChannelId', {
        extra: {
          component: 'useCopilotV2SwitchAblyThreads'
        }
      }));
    }

    // these actions will update the ablyChannelId in the `chat` redux store
    // when a new ablyChannelId is set, the AblyRealtimeClient.tsx will automatically setup connection to the new channel

    if (!threadId) {
      return dispatch(initializeAblyChannel({
        assistantType,
        customCopilotId
      })).then(response => {
        return handleAssistantSelection(assistantType, customCopilotId).then(() => response);
      });
    } else {
      if (isReadOnly) {
        return dispatch(copilotV1ThreadMessagesForV2(threadId));
      } else {
        return dispatch(copilotV2GetThreadWithMessages({
          threadId,
          messageLimit: THREAD_INITIAL_MESSAGES_COUNT_TO_FETCH_V2
        })).then(response => {
          return handleAssistantSelection(assistantType, customCopilotId).then(() => response);
        }).catch(e => {
          Raven.captureException(e, {
            extra: {
              component: 'useCopilotV2SwitchAblyThreads',
              action: 'copilotV2GetThreadWithMessages',
              copilotVersion: '2'
            }
          });
        });
      }
    }
  }, [ablyChannelId, dispatch, handleAssistantSelection]);
  return switchAblyThread;
};